import { useCallback, useEffect, useMemo, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client/react";
import {
  CompanyIdentitiesDocument,
  GlobalSearchDocument,
  SearchEntityType,
} from "@/graphql/generated/operations";

// Company search backed by genesis' globalSearch (the company search).
//
// The company is its own search entity type, so a hit is THE COMPANY rather than a person who
// happens to work there (§1). What the query matches is fixed by §2 and lives entirely in the
// database trigger: name at weight A, expertise labels and industry at C. The company's prose stays
// out of the index deliberately. The structured `companyFilter` narrows that result set further
// (industry, expertise, size) but cannot widen what is searchable: CompanyFilterInput has no
// summary field, so the prose §2 excluded stays out of reach through the filter as well.
//
// §3: search is lexical keyword finding, never matching. No match-service call, no requirement
// profile, no solver ranking. `ts_rank` over the vector is the whole ranking story.
//
// THERE IS A HYDRATION STEP, exactly as in useGlobalPersonSearch, and it exists for the logo. The
// index holds an id and a label and no image, so for a long time a company hit could only be drawn
// as a monogram while the person hit beside it showed a real face. Reading each hit back through
// CompanyProfile was the wrong repair — one round trip and one full profile per row — and the batch
// query to do it properly did not exist. `companyIdentities` is that query (genesis#263): one
// statement for the whole page, name and logo only, capped at 50 ids by the server.
//
// THE SEARCH STILL DECIDES WHAT IS FOUND. Hydration only draws what the search already returned: it
// takes ids, applies no filter and no ranking of its own, and a row whose hydration is missing keeps
// the label the index gave it. So this cannot widen or reorder a result set, and a company that is
// out of the index (an inactive one, the company search) is not reachable through it.
//
// Paging is offset-based (globalSearch has no cursors); loadMore advances skip and appends. The
// return shape mirrors usePagedQuery so it is drop-in for the list UI.

/**
 * The most ids one hydration round may carry — genesis's `CompanyController.MAX_COMPANY_IDENTITIES`,
 * mirrored. Over it the server refuses the whole call rather than truncating, so this is a hard
 * boundary and not a tuning knob: raising it here alone turns every logo on the page off.
 */
const MAX_HYDRATED_IDS = 50;

export interface CompanySearchHit {
  readonly id: string;
  readonly name: string;
  /**
   * The company's logo, or null while the hydration is still in flight AND when it really has none.
   *
   * The two are deliberately the same value: both render the monogram, and a row that told them
   * apart would have to flicker from a placeholder into an image. `ResultCard` takes exactly this
   * shape and falls back to the initials on its own.
   */
  readonly logo: { readonly mimeType?: string | null; readonly content?: string | null } | null;
}

interface GlobalCompanySearchResult {
  nodes: CompanySearchHit[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  loadMoreFailed: boolean;
  error: Error | undefined;
  loadMore: () => void;
  /**
   * One-shot fetch of the full ranked id set (up to `limit`) for the current query + filter,
   * bypassing pagination — the twin of `useGlobalPersonSearch.fetchAllIds`. `capped` is true when
   * more matched than `limit` could return (globalSearch overfetches limit+1 to derive it).
   *
   * <b>Why a bulk add cannot read `nodes` instead.</b> `nodes` is what has been PAGED IN, so a
   * collect button built on it takes one page and calls it "all" — which is what spark#293 was
   * filed for: at a page of 24, 185 companies needed eight "load more" clicks first.
   */
  fetchAllIds: (limit: number) => Promise<{ ids: string[]; capped: boolean }>;
}

/**
 * Ranked, de-duplicated company hits.
 *
 * genesis writes exactly one `global_search` row per company (`global_search_upsert('company', …)`
 * with a single attribute), so unlike the person path there is no fragment fan-out to collapse. The
 * de-duplication is still here because offset paging can hand back a row twice when the underlying
 * set shifts between pages — two rows with the same id would collide on the React key.
 */
function dedupeHits(
  results: ReadonlyArray<{ entityId: string; label?: string | null }>,
): CompanySearchHit[] {
  const seen = new Set<string>();
  const hits: CompanySearchHit[] = [];
  for (const r of results) {
    // A row whose label never resolved is a row that would render as an empty card. genesis makes
    // company.name NOT NULL precisely so this cannot happen; skipping is the honest response if it
    // ever does, rather than showing a nameless, unidentifiable result.
    if (!r.entityId || !r.label || seen.has(r.entityId)) continue;
    seen.add(r.entityId);
    hits.push({ id: r.entityId, name: r.label, logo: null });
  }
  return hits;
}

// Loose alias for the structured filter: buildCompanyWhere() emits a CompanyFilterInput-shaped
// object that the query layer casts at the call site (same pattern the rest of the search code uses).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CompanyFilter = Record<string, any>;

export function useGlobalCompanySearch(opts: {
  query: string;
  companyFilter?: CompanyFilter | null;
  pageSize?: number;
  skip?: boolean;
}): GlobalCompanySearchResult {
  const { query, companyFilter = null, pageSize = 24, skip = false } = opts;
  const trimmed = query.trim();
  const client = useApolloClient();

  // THE QUERY WITHOUT ITS WINDOW, split out so the paged read and the one-shot read below are
  // provably the same search. Paging and bulk-collecting differing by a stray filter is exactly the
  // class of bug that makes a button add the wrong set, and it cannot happen if the window is the
  // only thing that differs.
  const baseInput = useMemo(
    () => ({
      // Mirrors useGlobalPersonSearch: a blank term is a browse of every visible company (genesis
      // documents the query-optional path as "filter/entityType-only with rank 0"), not an error.
      query: trimmed.length > 0 ? trimmed : null,
      entityTypes: [SearchEntityType.Company],
      // genesis applies this server-side and intersects by company id before paging, so a page of
      // N is N MATCHING companies. Filtering client-side instead would make hasNextPage a lie and
      // leave short pages wherever the filter removed rows.
      companyFilter,
    }),
    [trimmed, companyFilter],
  );

  const searchInput = useMemo(
    () => ({ ...baseInput, skip: 0, take: pageSize }),
    [baseInput, pageSize],
  );

  const search = useQuery(GlobalSearchDocument, {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    variables: { input: searchInput as any },
    skip,
  });

  const results = useMemo(() => search.data?.globalSearch.results ?? [], [search.data]);
  const hasMore = search.data?.globalSearch.hasNextPage ?? false;
  const hits = useMemo(() => dedupeHits(results), [results]);

  // HYDRATED ONCE PER COMPANY, AND KEPT. `hits` is the ACCUMULATED result set — loadMore appends —
  // so asking for "the ids on screen" grows the request with every page: at a page of 24 the third
  // ask is 72 ids, genesis refuses anything over MAX_HYDRATED_IDS with a VALIDATION error, and
  // because a failed hydration is deliberately silent (see `error` below) every logo on the page
  // would vanish at once. So each round asks only for what is still unknown, and what came back is
  // remembered here rather than re-requested.
  const [logos, setLogos] = useState<ReadonlyMap<string, CompanySearchHit["logo"]>>(new Map());

  // Capped for the same reason genesis caps it, and at the same number: a page bigger than the cap
  // simply hydrates in more than one round, because `pending` recomputes from what is still missing
  // as soon as the previous round lands.
  const pending = useMemo(
    () => hits.filter((h) => !logos.has(h.id)).map((h) => h.id).slice(0, MAX_HYDRATED_IDS),
    [hits, logos],
  );

  const identities = useQuery(CompanyIdentitiesDocument, {
    variables: { input: { ids: pending } },
    // Nothing to hydrate before the search has answered, and nothing to ask once every row is
    // hydrated — an empty list would be a request that can only come back empty.
    skip: skip || pending.length === 0,
  });

  const answered = identities.data?.companyIdentities.companies;
  useEffect(() => {
    if (!answered) return;

    setLogos((previous) => {
      const next = new Map(previous);
      // EVERY ID ASKED FOR IS RECORDED, not only the ones that came back. An id genesis did not
      // answer for — a company deleted between the search and the hydration — would otherwise stay
      // "pending" forever and the query would re-issue itself on every render.
      for (const id of pending) next.set(id, null);
      for (const company of answered) next.set(company.id, company.logo ?? null);

      return next;
    });
    // `pending` is intentionally not a dependency: this runs on an ANSWER, and re-running it when
    // the ask changes would record the new ask as hydrated before it has been.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answered]);

  // THE SEARCH'S ORDER IS KEPT. The hydration answers in whatever order the database produced, and
  // `ts_rank` is the whole ranking story (the company search) — so the hits stay the list and the logo is
  // looked up per row, rather than the hydrated companies becoming the list. Rows already hydrated
  // keep their logo while a further page is in flight, because the map outlives the request.
  const nodes = useMemo(
    () => hits.map((hit) => ({ ...hit, logo: logos.get(hit.id) ?? null })),
    [hits, logos],
  );

  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    setLoadMoreFailed(false);
    search
      .fetchMore({
        // Advance by the RAW row count, not by nodes.length. Offset paging counts rows in the
        // database, and de-duplication happens after the fact — paging by the de-duplicated count
        // would re-request rows already consumed and stall on a repeating window.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        variables: { input: { ...searchInput, skip: results.length } as any },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          // `prev` CAN BE NULL, and the null has to be handled here rather than left to throw.
          // Apollo builds it with cache.readQuery, which ends in `… .result || null`, so a cache
          // entry that disappeared while this page was in flight — logout, resetStore, an eviction
          // on a context switch — arrives as null. Reading `prev.globalSearch` then throws INSIDE
          // Apollo's cache batch, where the failure is invisible: the fetchMore promise does not
          // reject, so `.catch` below never runs, `loadMoreFailed` stays false, the spinner clears
          // normally and the page just vanishes. A silent hole is worse than either a crash or an
          // error state, which is why this is a guard and not a try/catch.
          //
          // When the cache is gone the rows on screen came from it and are gone too, so there is
          // nothing to append TO: the fetched page becomes the whole list rather than being merged
          // into a list that no longer exists.
          const previous = prev?.globalSearch?.results ?? [];
          return {
            ...prev,
            globalSearch: {
              ...fetchMoreResult.globalSearch,
              results: [...previous, ...(fetchMoreResult.globalSearch?.results ?? [])],
            },
          };
        },
      })
      .catch(() => setLoadMoreFailed(true))
      .finally(() => setLoadingMore(false));
  }, [hasMore, loadingMore, search, searchInput, results.length]);

  /**
   * Every matching id in one read, up to `limit`.
   *
   * <p><b>`no-cache`, and for a narrower reason than it first looks.</b> It does NOT protect the
   * paged list from being overwritten: there is no `keyArgs` policy for `globalSearch`, so Apollo
   * keys this read by its own `skip`/`take` and the two windows are separate cache entries anyway.
   * What it buys is that a result of up to `limit` rows — 2000, none of which is rendered — is
   * never normalised and kept, and that a second collect re-reads instead of being served a set
   * the pool has meanwhile changed underneath. (If a pagination policy is ever added for this
   * field the windows WOULD merge, and then `no-cache` becomes the only thing keeping the list on
   * screen intact; `leaves the paged list untouched` in the test file guards that day.)
   *
   * <p><b>De-duplicated through `dedupeHits`, the same function the rendered rows use.</b> The
   * count on the collect button and the set the click adds have to be the one answer; resolving
   * them through different code is how they drift apart.
   */
  const fetchAllIds = useCallback(
    async (limit: number) => {
      const res = await client.query({
        query: GlobalSearchDocument,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        variables: { input: { ...baseInput, skip: 0, take: limit } as any },
        fetchPolicy: "no-cache",
      });
      return {
        ids: dedupeHits(res.data?.globalSearch.results ?? []).map((hit) => hit.id),
        capped: res.data?.globalSearch.hasNextPage ?? false,
      };
    },
    [client, baseInput],
  );

  return {
    nodes,
    // The SEARCH's first page only, deliberately not the hydration's. Unlike the person hook — where
    // hydration carries the name and a row without it is unreadable — a company row is complete
    // without its logo, so holding the whole list back for an image would trade a monogram that
    // becomes a logo for an empty screen that becomes a list.
    loading: search.loading && results.length === 0,
    loadingMore,
    hasMore,
    loadMoreFailed,
    // The hydration's error is NOT surfaced, for the same reason: it costs a monogram, not a row,
    // and turning the result list into an error page over a logo would be a worse answer than the
    // one the user can already read.
    error: search.error,
    loadMore,
    fetchAllIds,
  };
}
